/**
 * Global App Error Boundary
 *
 * Catches all unhandled errors in the React tree and displays a recovery UI.
 * Specifically handles:
 * - Chunk load failures (lazy imports that fail to load)
 * - Runtime errors in components
 * - Async errors caught by error boundaries
 *
 * Recovery options:
 * - Retry: Attempts to re-render the component tree
 * - Reload: Full page reload (clears all state)
 * - Go Home: Navigate to home page with full reload
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Copy, Check } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Callback when user resets the boundary */
  onReset?: () => void;
  /** Level of error boundary (for nested boundaries) */
  level?: 'app' | 'route' | 'component';
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  copied: boolean;
  isChunkError: boolean;
  retryCount: number;
}

// Detect chunk load errors (failed lazy imports)
function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('error loading dynamically imported module') ||
    error.message.includes('Unable to preload CSS') ||
    // Vite-specific chunk errors
    error.message.includes('Failed to load module script')
  );
}

export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      copied: false,
      isChunkError: false,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // Log error details
    console.error('[AppErrorBoundary] Caught error:', error);
    console.error('[AppErrorBoundary] Component stack:', errorInfo.componentStack);

    // For chunk errors, auto-retry once after a short delay
    if (isChunkLoadError(error) && this.state.retryCount === 0) {
      console.log('[AppErrorBoundary] Chunk load error detected, will auto-retry...');
      setTimeout(() => {
        this.handleRetry();
      }, 1000);
    }
  }

  handleRetry = () => {
    this.setState((prev) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      copied: false,
      retryCount: prev.retryCount + 1,
    }));
    this.props.onReset?.();
  };

  handleReload = () => {
    // Clear any cached state and do a full reload
    window.location.reload();
  };

  handleHardReload = () => {
    // Force reload bypassing cache
    // This helps when chunk files have changed on the server
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }
    // Clear session storage related to the app
    try {
      sessionStorage.clear();
    } catch (e) {
      // Ignore errors
    }
    // Force reload
    window.location.href = window.location.href.split('?')[0] + '?_=' + Date.now();
  };

  handleGoHome = () => {
    // Navigate to home with cache bust
    window.location.href = '/?_=' + Date.now();
  };

  handleCopyError = async () => {
    const { error, errorInfo } = this.state;
    const errorText = `
App Error Report
================
Time: ${new Date().toISOString()}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}

Error: ${error?.name}: ${error?.message}

Stack Trace:
${error?.stack}

Component Stack:
${errorInfo?.componentStack}
    `.trim();

    try {
      await navigator.clipboard.writeText(errorText);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch (e) {
      console.error('Failed to copy error:', e);
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Use custom fallback if provided
    if (this.props.fallback) {
      return this.props.fallback;
    }

    const { error, isChunkError, showDetails, copied, retryCount } = this.state;
    const level = this.props.level || 'app';

    // If we've retried too many times for a chunk error, show different message
    const maxRetries = isChunkError ? 2 : 1;
    const canRetry = retryCount < maxRetries;

    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="p-6 text-center border-b border-border">
            <div className="mx-auto mb-4 p-3 rounded-full bg-destructive/10 w-fit">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <h1 className="text-xl font-semibold text-foreground">
              {isChunkError ? 'Failed to Load Page' : 'Something Went Wrong'}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {isChunkError
                ? "The app couldn't load some required files. This usually happens after an update."
                : "The app encountered an unexpected error. Your data is safe."}
            </p>
          </div>

          {/* Error message */}
          <div className="p-4 bg-muted/50">
            <p className="text-sm text-center text-muted-foreground font-mono">
              {error?.message || 'An unknown error occurred'}
            </p>
          </div>

          {/* Actions */}
          <div className="p-4 space-y-3">
            {isChunkError ? (
              <>
                {/* For chunk errors, primary action is hard reload */}
                <button
                  onClick={this.handleHardReload}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-medium"
                >
                  <RefreshCw className="h-4 w-4" />
                  Reload App
                </button>
                <p className="text-xs text-center text-muted-foreground">
                  This will refresh the app and load the latest version.
                </p>
              </>
            ) : (
              <>
                {/* For other errors, offer retry first */}
                {canRetry && (
                  <button
                    onClick={this.handleRetry}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-medium"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </button>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={this.handleGoHome}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
                  >
                    <Home className="h-4 w-4" />
                    Go Home
                  </button>
                  <button
                    onClick={this.handleReload}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Reload
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Technical details (collapsible) */}
          <div className="border-t border-border">
            <button
              onClick={() => this.setState({ showDetails: !showDetails })}
              className="w-full px-4 py-2 text-xs text-muted-foreground hover:bg-muted/50 transition-colors text-center"
            >
              {showDetails ? 'Hide' : 'Show'} technical details
            </button>
            {showDetails && (
              <div className="p-4 pt-0 space-y-2">
                <div className="p-3 bg-muted rounded-md text-xs font-mono overflow-auto max-h-40">
                  <pre className="whitespace-pre-wrap break-words text-muted-foreground">
                    {error?.stack}
                  </pre>
                </div>
                <button
                  onClick={this.handleCopyError}
                  className="w-full flex items-center justify-center gap-2 px-3 py-1.5 text-xs border border-border rounded-md hover:bg-muted transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="h-3 w-3" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3" />
                      Copy error details
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Lightweight error boundary for route-level errors
 * Shows a smaller, less intrusive error UI
 */
export class RouteErrorBoundary extends AppErrorBoundary {
  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, isChunkError } = this.state;

    return (
      <div className="flex items-center justify-center min-h-[400px] p-6">
        <div className="text-center space-y-4">
          <div className="mx-auto p-3 rounded-full bg-destructive/10 w-fit">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <div>
            <h2 className="text-lg font-medium text-foreground">
              {isChunkError ? 'Page failed to load' : 'Error loading page'}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {isChunkError
                ? 'Try reloading the app to get the latest version.'
                : error?.message || 'An unexpected error occurred'}
            </p>
          </div>
          <div className="flex gap-2 justify-center">
            <button
              onClick={this.handleGoHome}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
            >
              <Home className="h-4 w-4" />
              Home
            </button>
            <button
              onClick={isChunkError ? this.handleHardReload : this.handleRetry}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              {isChunkError ? 'Reload' : 'Retry'}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
